<template>
  <UPopover
    :open="open"
    :content="{ side: 'bottom', align: 'start', collisionPadding: 12 }"
    @update:open="setOpen"
  >
    <button
      type="button"
      :disabled="disabled"
      :aria-label="$t('components.elements.email.link.title')"
      :aria-pressed="editor.isActive('link')"
      class="rounded-lg h-7 w-7 flex items-center justify-center transition hover:bg-primary-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
      :class="editor.isActive('link') ? 'bg-primary-900 text-white' : 'text-gray-700 dark:text-white/70'"
      @mousedown.prevent
    >
      <o-icon icon="link" :fill="true" :stroke="null" />
    </button>

    <template #content>
      <div class="w-80 max-w-[calc(100vw-2rem)] space-y-3 p-4">
        <p class="text-sm font-semibold">
          {{ $t('components.elements.email.link.title') }}
        </p>
        <UFormField :label="$t('components.elements.editor.actions.link.linkUrl')" :error="error || undefined">
          <UInput v-model="href" class="w-full" placeholder="https://" autofocus @update:model-value="error = ''" @keydown.enter.prevent.stop="save" />
        </UFormField>
        <UFormField :label="$t('components.elements.editor.actions.link.linkText')">
          <UInput v-model="text" class="w-full" @keydown.enter.prevent.stop="save" />
        </UFormField>
        <div class="flex items-center justify-between gap-2">
          <UButton v-if="existingLink" type="button" color="error" variant="ghost" @click="remove">
            {{ $t('components.elements.editor.actions.link.remove') }}
          </UButton>
          <div class="ml-auto flex gap-2">
            <UButton type="button" color="neutral" variant="ghost" @click="setOpen(false)">
              {{ $t('general.cancel') }}
            </UButton>
            <UButton type="button" :disabled="!href.trim()" @click="save">
              {{ $t('general.save') }}
            </UButton>
          </div>
        </div>
      </div>
    </template>
  </UPopover>
</template>

<script setup lang="ts">
import { getMarkRange } from '@tiptap/core'
import type { Editor } from '@tiptap/vue-3'
import { UButton, UFormField, UInput, UPopover } from '#components'

const props = defineProps<{ editor: Editor; disabled?: boolean }>()
const { t } = useI18n()
const open = ref(false)
const href = ref('')
const text = ref('')
const error = ref('')
const existingLink = ref(false)
let range = { from: 0, to: 0 }
let originalText = ''

function setOpen (value: boolean) {
  if (value) {
    const { selection, schema, doc } = props.editor.state
    existingLink.value = props.editor.isActive('link')
    range = (existingLink.value && getMarkRange(selection.$from, schema.marks.link!)) || {
      from: selection.from,
      to: selection.to,
    }
    originalText = doc.textBetween(range.from, range.to, '\n')
    text.value = originalText
    href.value = props.editor.getAttributes('link').href || ''
    error.value = ''
  }
  open.value = value
}

function save () {
  const url = href.value.trim()
  if (!url || props.editor.isDestroyed) return
  if (!props.editor.can().chain().setTextSelection(range).setLink({ href: url }).run()) {
    error.value = t('components.elements.editor.actions.link.invalidUrl')
    return
  }

  const chain = props.editor.chain().focus().setTextSelection(range).setLink({ href: url })
  // Keep rich text and placeholders intact when only the destination changes.
  if (text.value !== originalText || range.from === range.to) {
    chain.command(({ tr }) => {
      tr.insertText(text.value || url, range.from, range.to)
      return true
    })
  }
  if (chain.run()) open.value = false
}

function remove () {
  if (props.editor.isDestroyed) return
  props.editor.chain().focus().setTextSelection(range).unsetLink().run()
  open.value = false
}
</script>
